 "use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Search, Bold, Italic, Underline, List, ListOrdered, Loader2 } from "lucide-react";
import { useArchivalMode } from "../../contexts/ArchivalModeContext";

interface DeficiencyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (deficiencyId: string) => void;
}

interface ControlOption {
  id: string;
  label: string;
}

interface RichTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextField: React.FC<RichTextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
}) => {
  const { isArchivalMode } = useArchivalMode();
  const [internalValue, setInternalValue] = useState(value);
  const editorRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== internalValue) {
      editorRef.current.innerHTML = internalValue || "";
    }
  }, [internalValue]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-white">{label}</label>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 p-2 border border-white/10 rounded bg-black/40">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => document.execCommand("bold")}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => document.execCommand("italic")}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => document.execCommand("underline")}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
          >
            <Underline className="h-4 w-4" />
          </Button>
          <div className="w-px h-6 bg-white/20 mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => document.execCommand("insertUnorderedList")}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => document.execCommand("insertOrderedList")}
            className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </div>
        <div
          ref={editorRef}
          contentEditable={!isArchivalMode}
          className="min-h-[160px] p-3 border border-white/10 rounded bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-invert max-w-none"
          style={{
            fontFamily: "inherit",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
          data-placeholder={placeholder || "Enter your text here..."}
          onInput={(e) => {
            if (isArchivalMode) return;
            const html = (e.currentTarget as HTMLDivElement).innerHTML;
            setInternalValue(html);
            onChange(html);
          }}
        />
      </div>
    </div>
  );
};

export const DeficiencyFormModal: React.FC<DeficiencyFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { isArchivalMode } = useArchivalMode();
  const [isRelevant, setIsRelevant] = useState<string>("Yes");
  const [controlOptions, setControlOptions] = useState<ControlOption[]>([]);
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([]);
  const [controlSearch, setControlSearch] = useState("");
  const [deficiencyType, setDeficiencyType] = useState<string>("Design & Implementation");
  const [description, setDescription] = useState<string>("");
  const [rootCause, setRootCause] = useState<string>("");
  const [loadingControls, setLoadingControls] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load control IDs from Libraries_RCM.json via SharePoint
  useEffect(() => {
    const loadControls = async () => {
      if (!isOpen) return;
      setLoadingControls(true);
      try {
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function") {
          const res = await window.sharePointAPI.downloadRcmJson();
          if (res.success && res.data) {
            const data = res.data;
            const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
            const entries = Object.entries(librariesRcm) as [string, any][];
            const options: ControlOption[] = entries.map(([controlId, entry]) => ({
              id: controlId,
              label: `${controlId} - ${entry.control_name || entry.controlName || ""}`.trim(),
            }));
            setControlOptions(options);
          } else {
            setControlOptions([]);
          }
        } else {
          setControlOptions([]);
        }
      } catch (err) {
        console.error("Failed to load control IDs from Libraries_RCM.json", err);
        setControlOptions([]);
      } finally {
        setLoadingControls(false);
      }
    };

    loadControls();
  }, [isOpen]);

  const filteredControlOptions = useMemo(() => {
    const q = controlSearch.toLowerCase().trim();
    if (!q) return controlOptions;
    return controlOptions.filter(
      (opt) =>
        opt.id.toLowerCase().includes(q) ||
        opt.label.toLowerCase().includes(q),
    );
  }, [controlOptions, controlSearch]);

  const toggleControlSelection = (id: string) => {
    setSelectedControlIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const resetForm = () => {
    setIsRelevant("Yes");
    setSelectedControlIds([]);
    setControlSearch("");
    setDeficiencyType("Design & Implementation");
    setDescription("");
    setRootCause("");
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    try {
      setSaving(true);

      if (!window.sharePointAPI || typeof window.sharePointAPI.createDeficiency !== "function") {
        throw new Error("SharePoint API for deficiency creation is not available");
      }

      const payload = {
        control_relevance: isRelevant,
        control_id: selectedControlIds,
        deficiency_type: deficiencyType,
        description: description || "",
        root_cause: rootCause || "",
      };

      const result = await window.sharePointAPI.createDeficiency(payload);
      if (!result || !result.success || !result.data?.deficiencyId) {
        throw new Error(result?.error || "Failed to create deficiency");
      }

      const deficiencyId: string = result.data.deficiencyId;

      if (onCreated) {
        onCreated(deficiencyId);
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error("Failed to create deficiency", error);
      alert(
        `Failed to create deficiency: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-3xl bg-black/90 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Create Deficiency</DialogTitle>
          <DialogDescription className="text-white/60">
            Capture key details for the deficiency and link it to relevant controls.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {/* Control relevance */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">
              Is the control relevant to the audit?
            </label>
            <Select
              value={isRelevant}
              onValueChange={(val) => setIsRelevant(val)}
              disabled={isArchivalMode}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select relevance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Control IDs multi-select with search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">
              Control ID to which it is linked
            </label>
            <div className="rounded-md border border-white/20 bg-black/40">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                <Search className="h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search control ID or name..."
                  value={controlSearch}
                  onChange={(e) => setControlSearch(e.target.value)}
                  className="h-8 border-0 bg-transparent text-sm text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                  disabled={isArchivalMode}
                />
              </div>
              <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                {loadingControls ? (
                  <div className="flex items-center justify-center py-4 text-white/60 text-sm gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading controls from Libraries_RCM.json...
                  </div>
                ) : filteredControlOptions.length === 0 ? (
                  <div className="py-2 text-xs text-white/60 px-1">
                    No controls found. Ensure Libraries_RCM.json exists in SharePoint.
                  </div>
                ) : (
                  filteredControlOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggleControlSelection(opt.id)}
                      className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-white/10"
                      disabled={isArchivalMode}
                    >
                      <Checkbox
                        checked={selectedControlIds.includes(opt.id)}
                        className="h-4 w-4"
                        disabled={isArchivalMode}
                      />
                      <span className="truncate" title={opt.label}>
                        {opt.label}
                      </span>
                    </button>
                  ))
                )}
              </div>
              {selectedControlIds.length > 0 && (
                <div className="border-t border-white/10 px-3 py-2 text-xs text-white/70 flex flex-wrap gap-1">
                  <span className="font-medium mr-1">Selected:</span>
                  {selectedControlIds.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-[11px]"
                    >
                      {id}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Type of control deficiency */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">
              Type of control deficiency
            </label>
            <Select
              value={deficiencyType}
              onValueChange={(val) => setDeficiencyType(val)}
              disabled={isArchivalMode}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type of deficiency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Design & Implementation">
                  Design &amp; Implementation
                </SelectItem>
                <SelectItem value="Operating Effectiveness">
                  Operating Effectiveness
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <RichTextField
            label="Description of Deficiency"
            value={description}
            onChange={setDescription}
            placeholder="Describe the deficiency in detail..."
          />

          {/* Root cause */}
          <RichTextField
            label="Root Cause of Deficiency"
            value={rootCause}
            onChange={setRootCause}
            placeholder="Capture the underlying root cause..."
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            className="border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={saving}
            className="bg-blue-500 text-white hover:bg-blue-600"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


